import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { crafterUrl } from '../utils/urls';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Search, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CraftersPage() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [crafters, setCrafters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localSearchInput, setLocalSearchInput] = useState('');

  // Read directly from URL params
  const searchQuery = searchParams.get('search') || '';
  const selectedCategory = searchParams.get('category') || 'all';

  // Sync local input with URL on mount/URL change
  useEffect(() => {
    setLocalSearchInput(searchQuery);
  }, [searchQuery]);

  // Update category in URL
  const setSelectedCategory = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('category', value);
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams);
  };

  // Handle search submit from page input
  const handlePageSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (localSearchInput.trim()) {
      newParams.set('search', localSearchInput.trim());
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadCrafters();
  }, [searchParams]);

  const loadCategories = async () => {
    const { data } = await supabase
      .from('categories')
      .select('*')
      .order('name_it');

    if (data) setCategories(data);
  };

  const loadCrafters = async () => {
    setLoading(true);
    
    // Read fresh values from URL
    const currentSearch = searchParams.get('search') || '';
    const currentCategory = searchParams.get('category') || 'all';
    
    let query = supabase
      .from('profiles')
      .select('*')
      .or('role.eq.crafter,is_crafter_enabled.eq.true')
      .is('deleted_at', null)
      .eq('storefront_disabled', false);

    if (currentCategory !== 'all') {
      query = query.contains('categories', [currentCategory]);
    }

    if (currentSearch) {
      query = query.or(`full_name.ilike.%${currentSearch}%,bio.ilike.%${currentSearch}%,storefront_title.ilike.%${currentSearch}%`);
    }

    const { data } = await query.order('tier', { ascending: false });

    if (data) setCrafters(data);
    setLoading(false);
  };

  const clearSearch = () => {
    setLocalSearchInput('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="crafters-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-8">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#008080] mb-4" 
            style={{fontFamily: 'Inter, sans-serif'}}
          >
            Artigiani
          </h1>
          <p className="text-slate-600">Scopri i migliori artigiani italiani e le loro creazioni uniche</p>
        </div>

        {/* Active Search Indicator */}
        {searchQuery && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-[#008080]/5 border border-[#008080]/20 rounded-lg">
            <Search className="w-5 h-5 text-[#008080]" />
            <span className="text-slate-700">
              Risultati per: <strong className="text-[#008080]">"{searchQuery}"</strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="ml-auto text-slate-500 hover:text-red-500"
            >
              <X className="w-4 h-4 mr-1" />
              Cancella ricerca
            </Button>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white p-6 rounded-sm border border-slate-200 shadow-sm mb-8">
          <form onSubmit={handlePageSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Cerca artigiani..."
                value={localSearchInput}
                onChange={(e) => setLocalSearchInput(e.target.value)}
                className="pl-10"
                data-testid="page-search-input"
              />
            </div>
            <Button type="submit" className="bg-[#008080] hover:bg-[#006666]">
              <Search className="w-4 h-4 mr-2" />
              Cerca
            </Button>
          </form>

          {/* Category filters */}
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">{t('filter_by')}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-[#008080]' : ''}
                data-testid="filter-all"
              >
                Tutti
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.name_en ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.name_en)}
                  className={selectedCategory === cat.name_en ? 'bg-[#008080]' : ''}
                  data-testid={`filter-${cat.name_en.toLowerCase()}`}
                >
                  {language === 'it' ? cat.name_it : cat.name_en}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Crafters Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008080] mx-auto"></div>
          </div>
        ) : crafters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="crafters-grid">
            {crafters.map((crafter, index) => (
              <Link
                key={crafter.id}
                to={crafterUrl(crafter)}
                className="block"
                data-testid={`crafter-card-${index}`}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div
                    className="h-48 bg-gradient-to-br from-[#008080] to-[#00CED1]"
                    style={{
                      backgroundImage: crafter.cover_image ? `url(${crafter.cover_image})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  ></div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-14 h-14 rounded-full bg-slate-200 border-4 border-white -mt-10"
                        style={{
                          backgroundImage: crafter.avatar_url ? `url(${crafter.avatar_url})` : undefined,
                          backgroundSize: 'cover',
                        }}
                      ></div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{crafter.storefront_title || crafter.full_name || 'Artigiano'}</h3>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${
                            crafter.crafter_type === 'professionista' 
                              ? 'bg-[#008080]/10 text-[#008080] border border-[#008080]/30' 
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {crafter.crafter_type === 'professionista' ? 'Artigiano Professionista' : 'Hobbista'}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3 mb-3">
                      {crafter.bio || 'Artigiano specializzato in creazioni uniche e personalizzate.'}
                    </p>
                    {crafter.categories && crafter.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {crafter.categories.slice(0, 3).map((cat, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-sm border border-slate-200" data-testid="no-crafters-message">
            <p className="text-slate-600">Nessun artigiano trovato per questa categoria.</p>
          </div>
        )}
      </div>
    </div>
  );
}
